/**
 * <customizer-section>
 *
 * Owns the two bits of behaviour the Customizer section needs on top of the
 * theme's own <carousel-slider>:
 *
 *  1. Mobile: tapping a size card opens its detail panel instead of navigating,
 *     and swaps that card to its hover image (there is no :hover on touch).
 *  2. Desktop: makes the theme's grab-to-scroll actually usable. CarouselSlider
 *     already binds mousedown/mousemove and sets scrollLeft, but three things
 *     defeat it — the browser's native link/image drag steals the gesture,
 *     `.slider` carries `scroll-snap-type: x mandatory` (which snaps the
 *     scrollLeft we just set straight back), and a drag ends in a click that
 *     follows the card's link. The snap/native-drag half is handled in CSS
 *     (see the section's stylesheet); the trailing click is suppressed here.
 *  3. Measures the product image height into --customizer-img-h so the
 *     floating slider arrows sit on the image's centre, not the card's.
 */
const DRAG_CLICK_THRESHOLD = 5;
const HOVER_CLASS = 'is-showing-hover-image';

class CustomizerSection extends HTMLElement {
  connectedCallback() {
    this.mobileDetail = this.querySelector('.customizer-mobile-detail');
    this.panels = [...this.querySelectorAll('.customizer-mobile-panel')];
    this.slider = this.querySelector('.slider');

    this.addEventListener('click', this.handleClick.bind(this), true);

    if (this.slider) {
      this.slider.addEventListener('pointerdown', this.handlePointerdown.bind(this), true);
      this.slider.addEventListener('dragstart', (evt) => evt.preventDefault());
    }

    this.observeImageHeight();
  }

  disconnectedCallback() {
    if (this.imageObserver) this.imageObserver.disconnect();
    if (this.syncArrowOffset) window.removeEventListener('resize', this.syncArrowOffset);
  }

  /**
   * Tell the stylesheet how tall a product image is, so the arrows can sit on
   * its centre instead of the card's. It is measured rather than derived: the
   * height follows the card width, which moves with the breakpoint and with
   * the "products per row" setting, and the aspect ratio is a theme setting of
   * its own. The .img-ar box holds its height through an aspect-ratio
   * pseudo-element, so this reads correctly before the image has loaded.
   */
  observeImageHeight() {
    const frame = this.querySelector('.customizer-slider-frame');
    const firstImage = this.querySelector('.slider__item .img-ar');
    if (!frame || !firstImage) return;

    this.syncArrowOffset = () => {
      const height = firstImage.getBoundingClientRect().height;
      if (height > 0) frame.style.setProperty('--customizer-img-h', `${height}px`);
    };

    this.syncArrowOffset();
    if (window.ResizeObserver) {
      this.imageObserver = new ResizeObserver(this.syncArrowOffset);
      this.imageObserver.observe(firstImage);
    } else {
      window.addEventListener('resize', this.syncArrowOffset);
    }
  }

  handlePointerdown(evt) {
    this.pointerStart = { x: evt.clientX, y: evt.clientY };
    this.dragged = false;

    const onMove = (moveEvt) => {
      const dx = Math.abs(moveEvt.clientX - this.pointerStart.x);
      const dy = Math.abs(moveEvt.clientY - this.pointerStart.y);
      if (dx > DRAG_CLICK_THRESHOLD && dx > dy) this.dragged = true;
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      // Let the click that follows this pointerup see `dragged`, then reset.
      setTimeout(() => { this.dragged = false; }, 0);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }

  /**
   * Capture-phase, so it runs before the link's own default action.
   */
  handleClick(evt) {
    if (this.dragged) {
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }

    if (!this.isMobile()) return;

    const link = evt.target.closest('a.product-link');
    if (!link) return;

    const block = link.closest('product-block[data-customizer-index]');
    if (!block) return;

    evt.preventDefault();
    evt.stopPropagation();
    this.showHoverImage(block);
    this.openPanel(parseInt(block.dataset.customizerIndex, 10));
  }

  /**
   * Mobile stand-in for :hover — the tapped card reveals its hover image, any
   * previously tapped card reverts. The reveal itself is CSS (see the section
   * stylesheet); tapping the same card again is a no-op, matching the panel.
   */
  showHoverImage(block) {
    const target = block.querySelector('.image-cont--with-secondary-image');

    this.querySelectorAll(`.${HOVER_CLASS}`).forEach((el) => {
      if (el !== target) el.classList.remove(HOVER_CLASS);
    });

    if (target) target.classList.add(HOVER_CLASS);
  }

  openPanel(index) {
    if (!this.mobileDetail || !this.panels.length) return;

    this.mobileDetail.setAttribute('aria-hidden', 'false');
    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.removeAttribute('hidden');
        panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      } else {
        panel.setAttribute('hidden', '');
      }
    });
  }

  isMobile() {
    return window.matchMedia('(max-width: 768px)').matches;
  }
}

customElements.define('customizer-section', CustomizerSection);
